/**
 * Roadmap Hub (Unified List)
 *
 * Single entry point for all roadmap documents regardless of type.
 * Two roadmap kinds coexist here:
 *
 *   Strategic  — prerequisite-condition dependency tree (Tree / Grid / Split views)
 *   Horizon    — Now · Next · Later time-horizon planning (Horizon / Uncertainties / List views)
 *
 * Route: /prioritization/roadmap
 */

import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Plus,
  ArrowRight,
  Loader2,
  AlertCircle,
  Map,
  Network,
  ChevronDown,
  Calendar,
  Target,
  LayoutList,
  Table2,
  Trash2,
  Check,
  PenLine,
  Kanban,
} from 'lucide-react';
import { getItemCountState } from '../../components/collection/types';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { useRoadmapStore } from '../../stores/prioritization/roadmapStore';
import { useDesiredOutcomeStore } from '../../stores/prioritization/desiredOutcomeStore';
import {
  useProductRoadmapStore,
  type ProductRoadmapDocumentStatus,
} from '../../stores/prioritization/productRoadmapStore';
import { StrategyConnectivityHelpButton } from '../prioritization/strategy/StrategyConnectivityDocModal';

// ── Unified document type ──────────────────────────────────

type RoadmapKind = 'strategic' | 'horizon';

interface UnifiedRoadmap {
  id: string;
  kind: RoadmapKind;
  title: string;
  subtitle: string | null;     // objective (strategic) | description (horizon)
  futurePoint: string | null;  // strategic only
  definedSuccess: string | null; // strategic only
  status: ProductRoadmapDocumentStatus | null; // horizon only
  updatedAt: string;
  createdAt: string;
}

// ── Document-level status config (Horizon roadmaps) ────────

const DOC_STATUS_CONFIG: Record<
  ProductRoadmapDocumentStatus,
  { label: string; dotCls: string; badgeCls: string }
> = {
  draft: { label: 'Draft', dotCls: 'bg-[var(--surface)]', badgeCls: 'bg-[var(--hover-tint)] text-[var(--text-ter)]' },
  active: { label: 'Active', dotCls: 'bg-[var(--green)]', badgeCls: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/25 text-[var(--green)] dark:text-[var(--text-ter)]' },
  on_hold: { label: 'On Hold', dotCls: 'bg-[var(--mustard)]', badgeCls: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/25 text-[var(--mustard)] dark:text-[var(--mustard)]' },
  completed: { label: 'Completed', dotCls: 'bg-[var(--slate)]', badgeCls: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/25 text-[var(--text-sec)]' },
  archived: { label: 'Archived', dotCls: 'bg-[var(--rule-bright)]', badgeCls: 'bg-[var(--hover-tint)] text-[var(--text-ter)]' },
};
const STATUS_ORDER: ProductRoadmapDocumentStatus[] = ['draft', 'active', 'on_hold', 'completed', 'archived'];

function StatusBadge({
  status,
  onChange,
}: {
  status: ProductRoadmapDocumentStatus;
  onChange: (s: ProductRoadmapDocumentStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const cfg = DOC_STATUS_CONFIG[status];

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium hover:opacity-80 transition-opacity ${cfg.badgeCls}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />
        {cfg.label}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 w-36 bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] border border-[var(--rule)] shadow-lg py-1">
          {STATUS_ORDER.map((s) => {
            const c = DOC_STATUS_CONFIG[s];
            return (
              <button
                key={s}
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(s); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${c.dotCls}`} />
                <span className="text-[var(--text-sec)] dark:text-[var(--text-sec)]">{c.label}</span>
                {s === status && <span className="ml-auto text-[var(--text-ter)]">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Type badge ─────────────────────────────────────────────

function KindBadge({ kind }: { kind: RoadmapKind }) {
  if (kind === 'strategic') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)] font-medium">
        <Network className="h-2.5 w-2.5" />
        Strategic
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)] font-medium">
      <Kanban className="h-2.5 w-2.5" />
      Horizon
    </span>
  );
}

// ── New-roadmap type picker modal ──────────────────────────

interface NewRoadmapModalProps {
  outcomes: { id: string; title: string }[];
  onClose: () => void;
  onCreateStrategic: (title: string) => Promise<void>;
  onCreateHorizon: (title: string, description: string) => Promise<void>;
}

function NewRoadmapModal({ outcomes, onClose, onCreateStrategic, onCreateHorizon }: NewRoadmapModalProps) {
  const [step, setStep] = useState<'pick' | 'strategic' | 'horizon'>('pick');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customMode, setCustomMode] = useState(outcomes.length === 0);

  const handleStrategic = async () => {
    if (!title.trim()) return;
    setSaving(true);
    await onCreateStrategic(title.trim());
    setSaving(false);
  };

  const handleHorizon = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    await onCreateHorizon(title.trim(), description.trim());
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] shadow-2xl w-full max-w-lg mx-4">

        {/* Step 1: Choose type */}
        {step === 'pick' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">New Roadmap</h2>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-6">Choose the kind of roadmap you want to create.</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setStep('strategic')}
                className="group text-left p-4 border-2 border-[var(--slate)] dark:border-[var(--slate)]/60 hover:border-[var(--slate)] dark:hover:border-[var(--slate)] bg-[var(--slate-dim)]/50 dark:bg-[var(--slate)]/10 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-all"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40">
                    <Network className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
                  </div>
                  <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Strategic</span>
                </div>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] leading-relaxed">
                  Prerequisite-condition dependency tree. Ask <em>"what must be true?"</em> and map the path to a goal.
                </p>
              </button>

              <button
                type="button"
                onClick={() => setStep('horizon')}
                className="group text-left p-4 border-2 border-[var(--slate)] dark:border-[var(--slate)]/60 hover:border-[var(--slate)] dark:hover:border-[var(--slate)] bg-[var(--slate-dim)]/50 dark:bg-[var(--slate)]/10 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-all"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40">
                    <Kanban className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
                  </div>
                  <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Horizon</span>
                </div>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] leading-relaxed">
                  Now · Next · Later planning. Organize work by time horizon with confidence and uncertainty signals.
                </p>
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button type="button" onClick={onClose} className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] px-3 py-1.5">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Step 2a: Strategic */}
        {step === 'strategic' && (
          <div className="p-6">
            <button
              type="button"
              onClick={() => setStep('pick')}
              className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] mb-4"
            >
              ← Back
            </button>
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40">
                <Network className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
              </div>
              <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Strategic Roadmap</h2>
            </div>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-4">
              What future state are you planning toward?
            </p>

            {outcomes.length > 0 && !customMode && (
              <div className="relative mb-3">
                <button
                  type="button"
                  onClick={() => setPickerOpen((o) => !o)}
                  className={[
                    'w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm  border transition-colors text-left',
                    'bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-purple-500',
                    pickerOpen ? 'border-[var(--slate)]' : 'border-[var(--slate)] dark:border-[var(--slate)] hover:border-[var(--slate)]',
                  ].join(' ')}
                >
                  <span className="text-[var(--text-ter)] italic">Select an existing desired outcome…</span>
                  <ChevronDown className="h-4 w-4 flex-shrink-0 text-[var(--text-ter)]" />
                </button>
                {pickerOpen && (
                  <>
                    <div className="fixed inset-0 z-40" aria-hidden="true" onClick={() => setPickerOpen(false)} />
                    <div className="absolute z-50 mt-1 w-full border border-[var(--rule)] bg-[var(--bg-raised)] shadow-lg overflow-hidden">
                      <ul className="max-h-56 overflow-y-auto divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                        {outcomes.map((o) => (
                          <li key={o.id}>
                            <button
                              type="button"
                              onClick={() => { setPickerOpen(false); setTitle(o.title); setCustomMode(true); }}
                              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-left text-[var(--text-pri)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors"
                            >
                              <span className="truncate">{o.title}</span>
                              <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </div>
            )}

            {outcomes.length > 0 && !customMode && (
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px bg-[var(--slate-dim)] dark:bg-[var(--slate)]/60" />
                <button
                  type="button"
                  onClick={() => { setCustomMode(true); setTitle(''); }}
                  className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
                >
                  <PenLine className="h-3 w-3" />
                  Write my own
                </button>
                <div className="flex-1 h-px bg-[var(--slate-dim)] dark:bg-[var(--slate)]/60" />
              </div>
            )}

            {(outcomes.length === 0 || customMode) && (
              <div className="flex gap-2">
                <input
                  autoFocus
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleStrategic(); }}
                  placeholder="e.g. Launch v2, reach $10M ARR, expand to EMEA"
                  className="flex-1 px-3 py-2 text-sm border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-purple-100 dark:focus:ring-purple-900/40"
                />
                <button
                  type="button"
                  onClick={handleStrategic}
                  disabled={!title.trim() || saving}
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-60 text-white text-sm font-medium transition-colors"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Create
                </button>
              </div>
            )}

            <div className="mt-3 flex justify-end">
              <button type="button" onClick={onClose} className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Step 2b: Horizon */}
        {step === 'horizon' && (
          <form onSubmit={handleHorizon} className="p-6">
            <button
              type="button"
              onClick={() => setStep('pick')}
              className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] mb-4"
            >
              ← Back
            </button>
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40">
                <Kanban className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
              </div>
              <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Horizon Roadmap</h2>
            </div>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-4">
              Name this roadmap — a product area, team, or initiative.
            </p>
            <div className="space-y-3">
              <input
                autoFocus
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Platform Roadmap Q3, Mobile App 2026"
                className="w-full px-3 py-2 text-sm border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/40"
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description (optional)"
                rows={2}
                className="w-full px-3 py-2 text-sm border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/40 resize-none"
              />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <button type="button" onClick={onClose} className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">
                Cancel
              </button>
              <button
                type="submit"
                disabled={!title.trim() || saving}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-60 text-white text-sm font-medium transition-colors"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Create
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Card (one/few) ─────────────────────────────────────────

function RoadmapCard({
  doc,
  onDelete,
  onStatusChange,
}: {
  doc: UnifiedRoadmap;
  onDelete: (id: string, kind: RoadmapKind) => void;
  onStatusChange: (id: string, s: ProductRoadmapDocumentStatus) => void;
}) {
  const href = doc.kind === 'strategic'
    ? `/prioritization/roadmap/${doc.id}`
    : `/prioritization/product-roadmap/${doc.id}`;

  const Icon = doc.kind === 'strategic' ? Network : Map;
  const iconBg = doc.kind === 'strategic'
    ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)]'
    : 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)]';

  return (
    <div className="relative group/card border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] hover:shadow-md hover:border-[var(--rule)] dark:hover:border-[var(--rule)] transition-all">
      <Link to={href} className="block p-5 pr-10">
        <div className="flex items-start gap-3">
          <div className={`p-2  flex-shrink-0 ${iconBg}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <KindBadge kind={doc.kind} />
            </div>
            <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
              {doc.title}
            </h3>
            {doc.subtitle && (
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1 line-clamp-2">
                {doc.subtitle}
              </p>
            )}
            <div className="flex items-center gap-3 mt-2.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {doc.futurePoint && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {doc.futurePoint}
                </span>
              )}
              {doc.definedSuccess && (
                <span className="flex items-center gap-1">
                  <Target className="h-3 w-3" />
                  <span className="truncate max-w-[120px]">{doc.definedSuccess}</span>
                </span>
              )}
              {doc.status && (
                <StatusBadge
                  status={doc.status}
                  onChange={(s) => onStatusChange(doc.id, s)}
                />
              )}
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-[var(--text-ter)] group-hover/card:text-[var(--text-ter)] transition-colors flex-shrink-0 mt-0.5" />
        </div>
      </Link>
      <button
        type="button"
        onClick={() => onDelete(doc.id, doc.kind)}
        title="Delete"
        className="absolute top-3 right-8 p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20 opacity-0 group-hover/card:opacity-100 transition-all"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ── Table row (many) ───────────────────────────────────────

function RoadmapTableRow({
  doc,
  onDelete,
  deletingId,
  onStatusChange,
}: {
  doc: UnifiedRoadmap;
  onDelete: (id: string, kind: RoadmapKind) => void;
  deletingId: string | null;
  onStatusChange: (id: string, s: ProductRoadmapDocumentStatus) => void;
}) {
  const href = doc.kind === 'strategic'
    ? `/prioritization/roadmap/${doc.id}`
    : `/prioritization/product-roadmap/${doc.id}`;

  return (
    <tr className="border-b border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/40 transition-colors">
      <td className="px-4 py-2.5">
        <Link to={href} className="flex items-center gap-2 group/link">
          {doc.kind === 'strategic'
            ? <Network className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
            : <Map className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
          }
          <span className="text-sm font-medium text-[var(--text-pri)] group-hover/link:text-[var(--text-ter)] dark:group-hover/link:text-[var(--text-ter)] transition-colors truncate max-w-xs">
            {doc.title}
          </span>
        </Link>
        {doc.subtitle && (
          <p className="text-xs text-[var(--text-ter)] ml-5 mt-0.5 truncate max-w-sm">{doc.subtitle}</p>
        )}
        {/* Defined Success is drawn from md up; below it, it reads here (audit #43). */}
        {doc.definedSuccess && (
          <p className="md:hidden text-xs text-[var(--text-ter)] ml-5 mt-0.5 line-clamp-2 max-w-sm">
            Success: {doc.definedSuccess}
          </p>
        )}
      </td>
      <td className="px-3 py-2.5 w-28">
        <KindBadge kind={doc.kind} />
      </td>
      <td className="px-3 py-2.5 w-32">
        {doc.futurePoint
          ? <span className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)]"><Calendar className="h-3 w-3" />{doc.futurePoint}</span>
          : doc.status
            ? <StatusBadge status={doc.status} onChange={(s) => onStatusChange(doc.id, s)} />
            : <span className="text-xs text-[var(--text-ter)]">—</span>
        }
      </td>
      <td className="px-3 py-2.5 max-w-[160px] hidden md:table-cell">
        <span className="text-xs text-[var(--text-ter)] truncate block">
          {doc.definedSuccess || '—'}
        </span>
      </td>
      <td className="px-2 py-2.5 text-right">
        <button
          type="button"
          onClick={() => onDelete(doc.id, doc.kind)}
          disabled={deletingId === doc.id}
          title="Delete"
          className="inline-flex items-center justify-center p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20 transition-colors disabled:opacity-50"
        >
          {deletingId === doc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
        </button>
      </td>
      <td className="px-3 py-2.5 w-8">
        <Link to={href} className="inline-flex text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors">
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </td>
    </tr>
  );
}

// ── Main page ──────────────────────────────────────────────

export default function RoadmapList() {
  const navigate = useNavigate();
  const location = useLocation();
  const listNavState = location.state;

  const {
    documents: strategicDocs,
    isLoading: strategicLoading,
    error: strategicError,
    fetchDocuments: fetchStrategic,
    createRoadmap,
    deleteRoadmap,
  } = useRoadmapStore();

  const {
    documents: horizonDocs,
    isLoading: horizonLoading,
    error: horizonError,
    fetchDocuments: fetchHorizon,
    createProductRoadmap,
    deleteProductRoadmap,
    updateProductRoadmap,
  } = useProductRoadmapStore();

  const { outcomes, fetchOutcomes } = useDesiredOutcomeStore();

  const [showNew, setShowNew] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'table' | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchStrategic();
    fetchHorizon();
    fetchOutcomes();
  }, [fetchStrategic, fetchHorizon, fetchOutcomes]);

  // Combine into unified list sorted by updatedAt desc
  const unified: UnifiedRoadmap[] = [
    ...strategicDocs.map((d): UnifiedRoadmap => ({
      id: d.id,
      kind: 'strategic',
      title: d.title,
      subtitle: d.objective || null,
      futurePoint: d.futurePoint || null,
      definedSuccess: d.definedSuccess || null,
      status: null,
      updatedAt: d.updatedAt,
      createdAt: d.createdAt,
    })),
    ...horizonDocs.map((d): UnifiedRoadmap => ({
      id: d.id,
      kind: 'horizon',
      title: d.title,
      subtitle: d.description || null,
      futurePoint: null,
      definedSuccess: null,
      status: d.status,
      updatedAt: d.updatedAt,
      createdAt: d.createdAt,
    })),
  ].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const isLoading = strategicLoading || horizonLoading;
  const error = strategicError || horizonError;
  const countState = getItemCountState(unified.length);
  const effectiveView = viewMode ?? (countState === 'many' ? 'table' : 'cards');

  const handleCreateStrategic = async (title: string) => {
    const { roadmap, error: err } = await createRoadmap({ title });
    if (err || !roadmap) { alert(`Failed to create roadmap: ${err ?? 'Unknown error'}`); return; }
    setShowNew(false);
    navigate(`/prioritization/roadmap/${roadmap.id}`, { state: listNavState });
  };

  const handleCreateHorizon = async (title: string, description: string) => {
    const { roadmap, error: err } = await createProductRoadmap({ title, description });
    if (err || !roadmap) { alert(`Failed to create roadmap: ${err ?? 'Unknown error'}`); return; }
    setShowNew(false);
    navigate(`/prioritization/product-roadmap/${roadmap.id}`);
  };

  const handleDelete = async (id: string, kind: RoadmapKind) => {
    const label = unified.find((d) => d.id === id)?.title ?? 'this roadmap';
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    setDeletingId(id);
    if (kind === 'strategic') {
      await deleteRoadmap(id);
    } else {
      await deleteProductRoadmap(id);
    }
    setDeletingId(null);
  };

  const handleStatusChange = async (id: string, status: ProductRoadmapDocumentStatus) => {
    await updateProductRoadmap(id, { status });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Map className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30"
        title="Roadmaps"
        subtitle="Strategic dependency trees and horizon planning — all in one place"
        actions={
          <div className="flex items-center gap-2">
            <StrategyConnectivityHelpButton context="roadmap_list" />
            {unified.length > 0 && (
              <div className="flex items-center border border-[var(--rule)] overflow-hidden">
                <button
                  onClick={() => setViewMode('cards')}
                  title="Card view"
                  className={`p-1.5 transition-colors ${effectiveView === 'cards' ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'}`}
                >
                  <LayoutList className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  title="Table view"
                  className={`p-1.5 transition-colors ${effectiveView === 'table' ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'}`}
                >
                  <Table2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <button
              onClick={() => setShowNew(true)}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Roadmap
            </button>
          </div>
        }
      />

      {isLoading && (
        <div className="flex items-center justify-center py-16 text-[var(--text-ter)]">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading...
        </div>
      )}

      {!isLoading && error && (
        <div className="flex items-center gap-2 p-4 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] text-[var(--coral)] dark:text-[var(--coral)] text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Zero state */}
      {!isLoading && !error && countState === 'zero' && (
        <div className="text-center py-16 border border-dashed border-[var(--rule)]">
          <Map className="h-10 w-10 text-[var(--text-ter)] mx-auto mb-3" />
          <h3 className="text-base font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">No roadmaps yet</h3>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-6 max-w-sm mx-auto">
            Create a <strong>Strategic</strong> roadmap to map prerequisite conditions toward a goal, or a <strong>Horizon</strong> roadmap to organize work across Now, Next, and Later.
          </p>
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create your first roadmap
          </button>
        </div>
      )}

      {/* One state — single expanded card */}
      {!isLoading && !error && countState === 'one' && (
        <RoadmapCard
          doc={unified[0]}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Few / Many — cards or table */}
      {!isLoading && !error && (countState === 'few' || countState === 'many') && (
        <>
          {effectiveView === 'cards' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {unified.map((doc) => (
                <RoadmapCard
                  key={doc.id}
                  doc={doc}
                  onDelete={handleDelete}
                  onStatusChange={handleStatusChange}
                />
              ))}
            </div>
          )}

          {effectiveView === 'table' && (
            <div className="border border-[var(--rule)] overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-[var(--surface)] dark:bg-[var(--bg-raised)]/60 border-b border-[var(--rule)]">
                    <th className="px-4 py-2 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Roadmap</th>
                    <th className="px-3 py-2 w-28 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Type</th>
                    <th className="px-3 py-2 w-32 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Horizon / Status</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide hidden md:table-cell">Defined Success</th>
                    <th className="w-10 px-2 py-2" />
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {unified.map((doc) => (
                    <RoadmapTableRow
                      key={doc.id}
                      doc={doc}
                      onDelete={handleDelete}
                      deletingId={deletingId}
                      onStatusChange={handleStatusChange}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showNew && (
        <NewRoadmapModal
          outcomes={outcomes}
          onClose={() => setShowNew(false)}
          onCreateStrategic={handleCreateStrategic}
          onCreateHorizon={handleCreateHorizon}
        />
      )}
    </div>
  );
}
